/**
 * InlineChange — the pending change for one flag, under its Issues row.
 *
 * Shows the proposed paragraph as a word diff with Approve / Reject, so a single
 * flag is reviewed where it is listed. Decisions go through the shell's one
 * review queue (``approveById`` / ``rejectById``), so the Issues band and the
 * review window agree. Table and title-page changes have no inline diff here:
 * they open in the review window.
 *
 * SRP: presentation + callbacks only; never touches Word or the server.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { ProposalQueueItem } from "../../hooks/useProposalReviewFlow";
import { isTitleFieldProposal } from "../../hooks/useProposalReviewFlow";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";
import { DiffView } from "../DiffView";

const useStyles = makeStyles({
  root: {
    marginTop: "8px",
    padding: "8px 10px",
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "8px",
    backgroundColor: surfaceColors.paper,
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  head: { fontFamily: fonts.body, fontSize: "11px", fontWeight: 600, color: inkColors.ink3 },
  actions: { display: "flex", gap: "8px", alignItems: "center" },
  btn: {
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "6px",
    background: "transparent",
    padding: "3px 10px",
    fontFamily: fonts.body,
    fontSize: "11.5px",
    fontWeight: 600,
    color: inkColors.ink2,
    cursor: "pointer",
    ":disabled": { opacity: 0.6, cursor: "default" },
  },
  primary: {
    backgroundColor: brandColors.primary,
    borderTopColor: brandColors.primary,
    borderRightColor: brandColors.primary,
    borderBottomColor: brandColors.primary,
    borderLeftColor: brandColors.primary,
    color: "#fff",
  },
  link: {
    border: "none",
    background: "none",
    padding: 0,
    fontFamily: fonts.body,
    fontSize: "11.5px",
    fontWeight: 600,
    color: inkColors.ink3,
    cursor: "pointer",
    textDecorationLine: "underline",
  },
});

export interface InlineChangeProps {
  item: ProposalQueueItem;
  /** A decision is in flight (in this card or elsewhere in the queue). */
  busy?: boolean;
  onApprove: (proposalId: string) => void;
  onReject: (proposalId: string) => void;
  /** Open the change in the review window (edit, revise, or a table change). */
  onOpenReview: (proposalId: string) => void;
}

export const InlineChange: React.FC<InlineChangeProps> = ({ item, busy, onApprove, onReject, onOpenReview }) => {
  const styles = useStyles();
  const { proposal } = item;
  const isField = isTitleFieldProposal(proposal);
  const isTable = Boolean(proposal.is_table);
  return (
    <div className={styles.root} data-testid="inline-change">
      <span className={styles.head}>
        {isField
          ? `Proposed value for ${proposal.title_field_key}: ${proposal.proposed_text}`
          : isTable
            ? "Proposed table change"
            : "Proposed change"}
      </span>
      {!isField && !isTable ? (
        <DiffView before={proposal.original_text} after={proposal.proposed_text} maxHeight={140} hideSummary />
      ) : null}
      <div className={styles.actions}>
        {isTable ? (
          <button type="button" className={mergeClasses(styles.btn, styles.primary)} onClick={() => onOpenReview(proposal.id)}>
            Review table change
          </button>
        ) : (
          <>
            <button
              type="button"
              className={mergeClasses(styles.btn, styles.primary)}
              disabled={busy}
              onClick={() => onApprove(proposal.id)}
            >
              Approve
            </button>
            <button type="button" className={styles.btn} disabled={busy} onClick={() => onReject(proposal.id)}>
              Reject
            </button>
            <button type="button" className={styles.link} onClick={() => onOpenReview(proposal.id)}>
              Edit or revise…
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default InlineChange;
